<template>
  <div class="image-upload-container">
    <el-upload
      ref="uploadRef"
      :action="uploadUrl"
      :headers="uploadHeaders"
      list-type="picture-card"
      :on-preview="handlePreview"
      :on-success="handleSuccess"
      :on-remove="handleRemove"
      :on-error="handleError"
      :file-list="internalFileList"
      :disabled="disabled"
      accept="image/*"
      :multiple="multiple"
      :limit="limit"
      :on-exceed="handleExceed">
      <i class="el-icon-plus"></i>
    </el-upload>
    <div class="paste-tip" v-if="showTip && !disabled">
      <i class="el-icon-document-copy"></i>
      <span>支持 Ctrl+V 粘贴图片</span>
    </div>
  </div>
</template>

<script>
/**
 * 公共图片上传组件
 * 支持：点击上传、拖拽上传、全局 Ctrl+V 粑贴上传
 *
 * Props:
 * - value: 图片数据数组（v-model）
 *   - 当 returnObject=false 时：URL字符串数组 ['url1', 'url2']
 *   - 当 returnObject=true 时：对象数组 [{ url: 'url1', hash: 'hash1' }, ...]
 * - returnObject: 是否返回完整对象（包含hash），默认false
 * - disabled: 是否禁用
 * - multiple: 是否支持多选
 * - limit: 最大上传数量
 * - showTip: 是否显示粘贴提示
 * - active: 是否激活粘贴监听（通常绑定弹窗的 visible 状态）
 *
 * Events:
 * - input: 更新图片数据数组（v-model）
 * - preview: 图片预览事件，参数：(imageUrl, file)
 * - change: 图片列表变化事件
 * - upload-success: 单张图片上传成功事件，参数：{ url, hash }
 */

export default {
  name: 'ImageUpload',

  props: {
    // 图片数据数组，支持 v-model
    value: {
      type: Array,
      default: () => []
    },
    // 是否返回完整对象（包含hash），用于图片重复检测场景
    returnObject: {
      type: Boolean,
      default: false
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    },
    // 是否支持多选
    multiple: {
      type: Boolean,
      default: true
    },
    // 最大上传数量
    limit: {
      type: Number,
      default: 99
    },
    // 是否显示粘贴提示
    showTip: {
      type: Boolean,
      default: true
    },
    // 是否激活粘贴监听（绑定弹窗 visible）
    active: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      // 上传配置
      uploadUrl: this.$http.defaults.baseURL + 'upload',
      uploadHeaders: {
        authorization: window.sessionStorage.getItem('token'),
        Accept: 'application/json'
      },
      // 内部文件列表（用于 el-upload 显示）
      internalFileList: [],
      // 图片数据列表（内部维护）
      imageList: [],
      // 上传中的数量
      uploadingCount: 0
    }
  },

  watch: {
    // 监听外部 value 变化，同步到内部
    value: {
      handler (newVal) {
        if (!newVal || newVal.length === 0) {
          this.imageList = []
          this.internalFileList = []
          return
        }

        // 根据 returnObject 判断数据格式
        if (this.returnObject) {
          // 对象格式：[{ url, hash }]
          this.imageList = newVal.map(item => {
            if (typeof item === 'string') {
              return { url: item, hash: null }
            }
            return item
          })
        } else {
          // 字符串格式：['url1', 'url2']
          this.imageList = newVal.map(url => {
            if (typeof url === 'string') {
              return { url, hash: null }
            }
            return url
          })
        }
        this.syncFileList()
      },
      immediate: true,
      deep: true
    }
  },

  mounted () {
    // 注册全局粘贴事件监听
    this.bindPasteEvent()
  },

  destroyed () {
    // 移除全局粘贴事件监听
    this.unbindPasteEvent()
  },

  methods: {
    // 绑定粘贴事件
    bindPasteEvent () {
      this._pasteHandler = this.handlePaste.bind(this)
      document.addEventListener('paste', this._pasteHandler)
    },

    // 移除粘贴事件
    unbindPasteEvent () {
      if (this._pasteHandler) {
        document.removeEventListener('paste', this._pasteHandler)
        this._pasteHandler = null
      }
    },

    // 处理粘贴事件
    handlePaste (e) {
      // 未激活时不处理
      if (!this.active) return
      // 禁用时不处理
      if (this.disabled) return

      const items = e.clipboardData?.items
      if (!items) return

      // 遍历剪贴板内容，查找图片
      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile()
          if (file) {
            // 检查数量限制
            if (this.imageList.length + this.uploadingCount >= this.limit) {
              this.$message.warning(`最多上传 ${this.limit} 张图片`)
              return
            }
            // 上传图片
            this.uploadPasteImage(file)
          }
        }
      }
    },

    // 上传粘贴的图片
    async uploadPasteImage (file) {
      this.uploadingCount++

      // 生成临时预览
      const tempUid = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      const tempUrl = await this.getFilePreviewUrl(file)

      // 添加临时文件到列表显示
      const tempFile = {
        name: file.name || 'paste-image.png',
        url: tempUrl,
        uid: tempUid,
        status: 'uploading',
        percentage: 0
      }
      this.internalFileList.push(tempFile)

      try {
        // 构建 FormData
        const formData = new FormData()
        formData.append('file', file, 'paste-image.png')

        // 发送上传请求
        const { data: res } = await this.$http.post('upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            authorization: this.uploadHeaders.authorization,
            Accept: 'application/json'
          }
        })

        if (res.meta.status === 200) {
          // 上传成功，保存 url 和 hash
          const imageData = {
            url: res.data.url,
            hash: res.data.hash || null
          }

          // 更新内部数据
          this.imageList.push(imageData)
          this.emitUpdate()

          // 更新文件列表：替换临时文件
          const index = this.internalFileList.findIndex(f => f.uid === tempUid)
          if (index !== -1) {
            this.internalFileList[index] = {
              name: file.name || 'paste-image.png',
              url: this.getFullUrl(imageData.url),
              uid: tempUid,
              status: 'success',
              percentage: 100,
              response: res
            }
          }

          // 发送单张上传成功事件
          this.$emit('upload-success', imageData)
          this.$message.success('粘贴上传成功')
        } else {
          // 上传失败
          this.removeTempFile(tempUid)
          this.$message.error(res.meta.msg || '上传失败')
        }
      } catch (err) {
        console.error('粘贴上传失败:', err)
        this.removeTempFile(tempUid)
        this.$message.error('上传失败，请重试')
      } finally {
        this.uploadingCount--
      }
    },

    // 获取文件预览 URL
    getFilePreviewUrl (file) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.readAsDataURL(file)
      })
    },

    // 移除临时文件
    removeTempFile (uid) {
      const index = this.internalFileList.findIndex(f => f.uid === uid)
      if (index !== -1) {
        this.internalFileList.splice(index, 1)
      }
    },

    // 获取完整 URL
    getFullUrl (url) {
      const base = window.sessionStorage.getItem('base') || ''
      if (url && url.startsWith('/')) {
        return base + url
      }
      return url
    },

    // 从 URL 提取相对路径
    getRelativeUrl (url) {
      const base = window.sessionStorage.getItem('base') || ''
      if (url && url.startsWith(base)) {
        return url.substring(base.length)
      }
      return url
    },

    // 同步文件列表（从 imageList）
    syncFileList () {
      this.internalFileList = this.imageList.map((item, index) => ({
        name: `image-${index + 1}`,
        url: this.getFullUrl(item.url),
        uid: `url_${index}_${Date.now()}`,
        status: 'success'
      }))
    },

    // 发送更新事件（根据 returnObject 决定格式）
    emitUpdate () {
      if (this.returnObject) {
        // 返回对象数组
        this.$emit('input', this.imageList)
        this.$emit('change', this.imageList)
      } else {
        // 返回 URL 字符串数组（向后兼容）
        const urls = this.imageList.map(item => item.url)
        this.$emit('input', urls)
        this.$emit('change', urls)
      }
    },

    // el-upload 上传成功回调
    handleSuccess (res, file, fileList) {
      if (res.meta.status !== 200) {
        this.$message.error(res.meta.msg || '上传失败')
        // 移除失败的文件
        const index = fileList.findIndex(f => f.uid === file.uid)
        if (index !== -1) {
          fileList.splice(index, 1)
        }
        this.internalFileList = fileList
        return
      }

      // 上传成功，保存 url 和 hash
      const imageData = {
        url: res.data.url,
        hash: res.data.hash || null
      }

      // 检查是否已存在（按 url 匹配）
      const exists = this.imageList.some(item => item.url === imageData.url)
      if (!exists) {
        this.imageList.push(imageData)
        this.emitUpdate()
        // 发送单张上传成功事件
        this.$emit('upload-success', imageData)
      }

      this.internalFileList = fileList
    },

    // el-upload 移除回调
    handleRemove (file, fileList) {
      // 获取要移除的 URL
      let removeUrl = ''
      if (file.response && file.response.data && file.response.data.url) {
        removeUrl = file.response.data.url
      } else if (file.url) {
        removeUrl = this.getRelativeUrl(file.url)
      }

      // 从 imageList 中移除（按 url 匹配）
      const index = this.imageList.findIndex(item => item.url === removeUrl)
      if (index !== -1) {
        this.imageList.splice(index, 1)
        this.emitUpdate()
      }

      this.internalFileList = fileList
    },

    // el-upload 上传失败回调
    handleError (err, file, fileList) {
      console.error('上传失败:', err)
      this.$message.error('上传失败，请重试')

      // 移除失败的文件
      const index = fileList.findIndex(f => f.uid === file.uid)
      if (index !== -1) {
        fileList.splice(index, 1)
      }
      this.internalFileList = fileList
    },

    // el-upload 超出数量限制回调
    handleExceed (files, fileList) {
      this.$message.warning(`最多上传 ${this.limit} 张图片，当前已上传 ${fileList.length} 张`)
    },

    // el-upload 预览回调
    handlePreview (file) {
      let imageUrl = ''
      if (file.url) {
        imageUrl = file.url
      } else if (file.response && file.response.data && file.response.data.url) {
        imageUrl = this.getFullUrl(file.response.data.url)
      }

      if (imageUrl) {
        this.$emit('preview', imageUrl, file)
      }
    },

    /**
     * 公开方法：手动添加图片
     * 供父组件调用，用于外部添加已上传的图片（如自动截图后添加）
     * @param {String} imageUrl - 图片相对 URL
     * @param {String} hash - 图片 hash 值（可选）
     * @param {String} name - 图片名称（可选）
     */
    addImageUrl (imageUrl, hash = null, name = 'image') {
      if (!imageUrl) return

      // 检查是否已存在（按 url 匹配）
      if (this.imageList.some(item => item.url === imageUrl)) {
        return
      }

      // 检查数量限制
      if (this.imageList.length >= this.limit) {
        this.$message.warning(`最多上传 ${this.limit} 张图片`)
        return
      }

      // 添加到数据列表
      const imageData = { url: imageUrl, hash }
      this.imageList.push(imageData)
      this.emitUpdate()

      // 同步文件列表
      const newFile = {
        name: name,
        url: this.getFullUrl(imageUrl),
        uid: `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        status: 'success'
      }
      this.internalFileList.push(newFile)

      // 发送单张上传成功事件
      this.$emit('upload-success', imageData)
      this.$message.success('图片已添加')
    }
  }
}
</script>

<style scoped>
.image-upload-container {
  display: inline-block;
}

.paste-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.paste-tip i {
  font-size: 14px;
}
</style>
